import { useState, useEffect, useCallback } from "react";
import {
    Cpu,
    BarChart3,
    ArrowUpRight,
    ArrowDownRight,
    Sparkles,
    Calendar,
    Filter,
    TrendingUp,
    Layers,
    Box,
    ChevronDown,
    ChevronRight,
    Activity,
    Zap,
    RefreshCw,
    X,
    ExternalLink,
} from "lucide-react";
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Legend,
} from "recharts";
import apiClient from "../../../api/api";
import { API_ENDPOINTS } from "../../../api/api.config";

interface TokenAnalyticsData {
    total_templates: number;
    total_executions: number;
    total_tokens: number;
    total_prompt_tokens: number;
    total_completion_tokens: number;
    avg_tokens_per_execution: number;
    model_breakdown: {
        model_name: string;
        total_tokens: number;
        total_prompt_tokens: number;
        total_completion_tokens: number;
        usage_count: number;
    }[];
    node_type_breakdown: {
        node_type: string;
        model_name: string;
        total_tokens: number;
        total_prompt_tokens: number;
        total_completion_tokens: number;
        usage_count: number;
    }[];
    execution_groups: {
        template_id: string;
        execution_count: number;
        total_tokens: number;
        total_prompt_tokens: number;
        total_completion_tokens: number;
        model_breakdown: {
            model_name: string;
            total_tokens: number;
            total_prompt_tokens: number;
            total_completion_tokens: number;
            usage_count: number;
        }[];
        node_type_breakdown: {
            node_type: string;
            model_name: string;
            total_tokens: number;
            total_prompt_tokens: number;
            total_completion_tokens: number;
            usage_count: number;
        }[];
    }[];
}

const MODEL_COLORS: Record<string, string> = {
    "gemini-3-flash-preview": "#2563EB",
    "gemini-2.5-flash": "#0891B2",
    "gemini-2.0-flash": "#059669",
    "gemini-pro": "#D97706",
};

const CHART_COLORS = [
    "#2563EB",
    "#0891B2",
    "#059669",
    "#D97706",
    "#DC2626",
    "#7C3AED",
    "#4F46E5",
    "#0D9488",
];

const NODE_TYPE_NAMES: Record<string, string> = {
    "GEMINI-UNDERSTANDING": "Smart Extraction",
    "SMART-PARSE": "Document Parsing",
    "SUMMARY": "LLM Node",
    "FILE-OCR": "OCR Extraction",
};

const formatTokenCount = (count: number): string => {
    if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(2)}M`;
    if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
    return count.toLocaleString();
};

const formatDate = (date: Date): string => {
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
};

const toInputDate = (date: Date): string => {
    return date.toISOString().split("T")[0];
};

interface Props {
    onClose: () => void;
    workflowNames?: Record<string, string>;
}

const TokenAnalyticsDashboard = ({ onClose, workflowNames = {} }: Props) => {
    const [data, setData] = useState<TokenAnalyticsData | null>(null);
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState<string>(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return toInputDate(d);
    });
    const [toDate, setToDate] = useState<string>(() => toInputDate(new Date()));
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
        new Set()
    );

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const from = formatDate(new Date(fromDate));
            const to = formatDate(new Date(toDate));
            const response = await apiClient.post(
                API_ENDPOINTS.WORKFLOW.TOKEN_ANALYSIS,
                { from_date: from, to_date: to }
            );
            if (response.data?.success && response.data?.data) {
                setData(response.data.data);
            }
        } catch (err) {
            console.error("Token analysis fetch failed:", err);
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate]);

    useEffect(() => {
        fetchData();
    }, []);

    const toggleGroup = (id: string) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const getModelColor = (model: string, index: number) =>
        MODEL_COLORS[model] || CHART_COLORS[index % CHART_COLORS.length];

    // Prepare chart data
    const pieData =
        data?.model_breakdown.map((m) => ({
            name: m.model_name,
            value: m.total_tokens,
            usage_count: m.usage_count,
        })) || [];

    const barData =
        data?.node_type_breakdown.map((n) => ({
            name: NODE_TYPE_NAMES[n.node_type] || n.node_type,
            model: n.model_name,
            prompt: n.total_prompt_tokens,
            completion: n.total_completion_tokens,
            total: n.total_tokens,
            count: n.usage_count,
        })) || [];

    const tokenDistribution = data
        ? [
              { name: "Prompt", value: data.total_prompt_tokens, color: "#2563EB" },
              {
                  name: "Completion",
                  value: data.total_completion_tokens,
                  color: "#0891B2",
              },
              {
                  name: "Other",
                  value:
                      data.total_tokens -
                      data.total_prompt_tokens -
                      data.total_completion_tokens,
                  color: "#E2E8F0",
              },
          ].filter((d) => d.value > 0)
        : [];

    const promptRatio = data
        ? ((data.total_prompt_tokens / data.total_tokens) * 100).toFixed(1)
        : "0";
    const completionRatio = data
        ? ((data.total_completion_tokens / data.total_tokens) * 100).toFixed(1)
        : "0";

    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex flex-col p-6">
            <div className="w-full h-full flex flex-col overflow-hidden rounded-2xl shadow-2xl">
                {/* Header */}
                <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
                            <Cpu className="h-4.5 w-4.5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-base font-semibold text-slate-900">
                                Token Usage Analytics
                            </h2>
                            <p className="text-slate-500 text-xs">
                                All workflows analytics &middot; Monitor AI token consumption across all workflows
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <a
                            href="https://www.llm-prices.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                            <ExternalLink className="h-3.5 w-3.5" />
                            LLM Price Calculator
                        </a>
                        <button
                            onClick={onClose}
                            className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-2 rounded-lg transition-colors"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div className="bg-white flex-1 overflow-y-auto">
                    {/* Date Filter Bar */}
                    <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 sticky top-0 z-10">
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Filter className="h-4 w-4" />
                                <span className="font-medium">Date Range:</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="relative">
                                    <Calendar className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                                    />
                                </div>
                                <span className="text-slate-400 text-xs">to</span>
                                <div className="relative">
                                    <Calendar className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                                    />
                                </div>
                            </div>
                            <button
                                onClick={fetchData}
                                disabled={loading}
                                className="flex items-center gap-1.5 bg-blue-600 text-white text-sm font-medium px-4 py-1.5 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                                <RefreshCw
                                    className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
                                />
                                Apply
                            </button>
                            {/* Quick date presets */}
                            <div className="flex items-center gap-1 ml-auto">
                                {[
                                    { label: "Today", days: 0 },
                                    { label: "7D", days: 7 },
                                    { label: "30D", days: 30 },
                                    { label: "90D", days: 90 },
                                ].map((preset) => (
                                    <button
                                        key={preset.label}
                                        onClick={() => {
                                            const to = new Date();
                                            const from = new Date();
                                            from.setDate(from.getDate() - preset.days);
                                            setFromDate(toInputDate(from));
                                            setToDate(toInputDate(to));
                                        }}
                                        className="text-xs px-2.5 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors"
                                    >
                                        {preset.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {data && (
                            <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full">
                                    <Calendar className="h-3 w-3" />
                                    {new Date(fromDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                                    {" "}&mdash;{" "}
                                    {new Date(toDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                                </span>
                                <span className="text-slate-400">|</span>
                                <span>{data.total_templates} workflows &middot; {data.total_executions} executions &middot; {data.total_tokens.toLocaleString("en-IN")} total tokens</span>
                            </div>
                        )}
                    </div>

                    {loading && (
                        <div className="flex items-center justify-center py-20">
                            <RefreshCw className="h-6 w-6 text-blue-500 animate-spin" />
                            <span className="ml-2 text-sm text-slate-500">
                                Loading analytics...
                            </span>
                        </div>
                    )}

                    {!loading && data && (
                        <div className="p-6 space-y-6">
                            {/* Summary Cards */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {/* Total Tokens */}
                                <div className="bg-white border border-slate-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center">
                                            <Cpu className="h-4 w-4 text-blue-600" />
                                        </div>
                                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                                            Total Tokens
                                        </span>
                                    </div>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {formatTokenCount(data.total_tokens)}
                                    </p>
                                    <p className="text-xs text-slate-400 mt-1">
                                        {data.total_tokens.toLocaleString()} tokens
                                    </p>
                                </div>

                                {/* Prompt Tokens */}
                                <div className="bg-white border border-slate-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center">
                                            <ArrowUpRight className="h-4 w-4 text-blue-600" />
                                        </div>
                                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                                            Prompt
                                        </span>
                                    </div>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {formatTokenCount(data.total_prompt_tokens)}
                                    </p>
                                    <div className="flex items-center gap-1.5 mt-2">
                                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-blue-500 rounded-full"
                                                style={{ width: `${promptRatio}%` }}
                                            />
                                        </div>
                                        <span className="text-xs text-slate-500">{promptRatio}%</span>
                                    </div>
                                </div>

                                {/* Completion Tokens */}
                                <div className="bg-white border border-slate-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-8 h-8 rounded-md bg-teal-50 flex items-center justify-center">
                                            <ArrowDownRight className="h-4 w-4 text-teal-600" />
                                        </div>
                                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                                            Completion
                                        </span>
                                    </div>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {formatTokenCount(data.total_completion_tokens)}
                                    </p>
                                    <div className="flex items-center gap-1.5 mt-2">
                                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-teal-500 rounded-full"
                                                style={{ width: `${completionRatio}%` }}
                                            />
                                        </div>
                                        <span className="text-xs text-slate-500">
                                            {completionRatio}%
                                        </span>
                                    </div>
                                </div>

                                {/* Avg per Execution */}
                                <div className="bg-white border border-slate-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-8 h-8 rounded-md bg-amber-50 flex items-center justify-center">
                                            <TrendingUp className="h-4 w-4 text-amber-600" />
                                        </div>
                                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                                            Avg / Execution
                                        </span>
                                    </div>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {formatTokenCount(data.avg_tokens_per_execution)}
                                    </p>
                                    <p className="text-xs text-slate-400 mt-1">
                                        {data.total_executions} executions across{" "}
                                        {data.total_templates} templates
                                    </p>
                                </div>
                            </div>

                            {/* Charts Row */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Model Breakdown Donut */}
                                <div className="border border-slate-200 rounded-xl p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Sparkles className="h-4 w-4 text-blue-500" />
                                        <h3 className="text-sm font-semibold text-slate-800">
                                            Token Usage by Model
                                        </h3>
                                    </div>
                                    <div className="flex items-center">
                                        <div className="w-1/2 h-48">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={pieData}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={45}
                                                        outerRadius={75}
                                                        paddingAngle={3}
                                                        dataKey="value"
                                                        stroke="none"
                                                    >
                                                        {pieData.map((_, index) => (
                                                            <Cell
                                                                key={index}
                                                                fill={
                                                                    CHART_COLORS[
                                                                        index % CHART_COLORS.length
                                                                    ]
                                                                }
                                                            />
                                                        ))}
                                                    </Pie>
                                                    <RechartsTooltip
                                                        formatter={(value: number) =>
                                                            formatTokenCount(value)
                                                        }
                                                        contentStyle={{
                                                            borderRadius: "8px",
                                                            border: "1px solid #e2e8f0",
                                                            fontSize: "12px",
                                                        }}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                        <div className="w-1/2 space-y-3">
                                            {data.model_breakdown.map((model, i) => (
                                                <div key={model.model_name} className="flex items-start gap-2">
                                                    <div
                                                        className="w-3 h-3 rounded-full mt-0.5 flex-shrink-0"
                                                        style={{
                                                            backgroundColor: getModelColor(
                                                                model.model_name,
                                                                i
                                                            ),
                                                        }}
                                                    />
                                                    <div className="min-w-0">
                                                        <p className="text-xs font-medium text-slate-700 truncate">
                                                            {model.model_name}
                                                        </p>
                                                        <p className="text-xs text-slate-500">
                                                            {formatTokenCount(model.total_tokens)}{" "}
                                                            tokens
                                                            <span className="text-slate-400">
                                                                {" "}
                                                                ({model.usage_count} calls)
                                                            </span>
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Token Distribution */}
                                <div className="border border-slate-200 rounded-xl p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Activity className="h-4 w-4 text-slate-500" />
                                        <h3 className="text-sm font-semibold text-slate-800">
                                            Prompt vs Completion Distribution
                                        </h3>
                                    </div>
                                    <div className="flex items-center">
                                        <div className="w-1/2 h-48">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={tokenDistribution}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={45}
                                                        outerRadius={75}
                                                        paddingAngle={3}
                                                        dataKey="value"
                                                        stroke="none"
                                                    >
                                                        {tokenDistribution.map((entry, index) => (
                                                            <Cell
                                                                key={index}
                                                                fill={entry.color}
                                                            />
                                                        ))}
                                                    </Pie>
                                                    <RechartsTooltip
                                                        formatter={(value: number) =>
                                                            formatTokenCount(value)
                                                        }
                                                        contentStyle={{
                                                            borderRadius: "8px",
                                                            border: "1px solid #e2e8f0",
                                                            fontSize: "12px",
                                                        }}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                        <div className="w-1/2 space-y-4">
                                            {tokenDistribution
                                                .filter((d) => d.name !== "Other")
                                                .map((d) => (
                                                    <div key={d.name}>
                                                        <div className="flex items-center justify-between mb-1">
                                                            <div className="flex items-center gap-2">
                                                                <div
                                                                    className="w-2.5 h-2.5 rounded-full"
                                                                    style={{
                                                                        backgroundColor: d.color,
                                                                    }}
                                                                />
                                                                <span className="text-xs font-medium text-slate-600">
                                                                    {d.name}
                                                                </span>
                                                            </div>
                                                            <span className="text-xs font-bold text-slate-800">
                                                                {formatTokenCount(d.value)}
                                                            </span>
                                                        </div>
                                                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                                            <div
                                                                className="h-full rounded-full transition-all"
                                                                style={{
                                                                    width: `${data ? (d.value / data.total_tokens) * 100 : 0}%`,
                                                                    backgroundColor: d.color,
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Node Type Breakdown Bar Chart */}
                            {barData.length > 0 && (
                                <div className="border border-slate-200 rounded-xl p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Box className="h-4 w-4 text-slate-500" />
                                        <h3 className="text-sm font-semibold text-slate-800">
                                            Token Usage by Node Type
                                        </h3>
                                    </div>
                                    <div className="h-64">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={barData}
                                                margin={{
                                                    top: 5,
                                                    right: 20,
                                                    left: 10,
                                                    bottom: 5,
                                                }}
                                            >
                                                <CartesianGrid
                                                    strokeDasharray="3 3"
                                                    stroke="#f1f5f9"
                                                />
                                                <XAxis
                                                    dataKey="name"
                                                    tick={{ fontSize: 11, fill: "#64748b" }}
                                                    axisLine={{ stroke: "#e2e8f0" }}
                                                />
                                                <YAxis
                                                    tick={{ fontSize: 11, fill: "#64748b" }}
                                                    axisLine={{ stroke: "#e2e8f0" }}
                                                    tickFormatter={(v) => formatTokenCount(v)}
                                                />
                                                <RechartsTooltip
                                                    formatter={(value: number, name: string) => [
                                                        formatTokenCount(value),
                                                        name === "prompt"
                                                            ? "Prompt Tokens"
                                                            : "Completion Tokens",
                                                    ]}
                                                    contentStyle={{
                                                        borderRadius: "8px",
                                                        border: "1px solid #e2e8f0",
                                                        fontSize: "12px",
                                                    }}
                                                />
                                                <Legend
                                                    formatter={(value) =>
                                                        value === "prompt"
                                                            ? "Prompt"
                                                            : "Completion"
                                                    }
                                                    iconSize={10}
                                                    wrapperStyle={{ fontSize: "12px" }}
                                                />
                                                <Bar
                                                    dataKey="prompt"
                                                    fill="#2563EB"
                                                    radius={[4, 4, 0, 0]}
                                                    maxBarSize={60}
                                                />
                                                <Bar
                                                    dataKey="completion"
                                                    fill="#0891B2"
                                                    radius={[4, 4, 0, 0]}
                                                    maxBarSize={60}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            )}

                            {/* Per-Workflow Execution Groups */}
                            <div className="border border-slate-200 rounded-xl overflow-hidden">
                                <div className="px-5 py-4 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                                    <Layers className="h-4 w-4 text-slate-500" />
                                    <h3 className="text-sm font-semibold text-slate-800">
                                        Per-Workflow Token Breakdown
                                    </h3>
                                    <span className="ml-auto text-xs text-slate-500">
                                        {data.execution_groups.length} workflows
                                    </span>
                                </div>

                                {/* Table Header */}
                                <div className="grid grid-cols-12 gap-2 px-5 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wide border-b border-slate-100 bg-white">
                                    <div className="col-span-4">Workflow</div>
                                    <div className="col-span-2 text-right">Executions</div>
                                    <div className="col-span-2 text-right">Prompt</div>
                                    <div className="col-span-2 text-right">Completion</div>
                                    <div className="col-span-2 text-right">Total</div>
                                </div>

                                {/* Rows */}
                                <div className="divide-y divide-slate-50">
                                    {data.execution_groups
                                        .sort((a, b) => b.total_tokens - a.total_tokens)
                                        .map((group) => {
                                            const isExpanded = expandedGroups.has(
                                                group.template_id
                                            );
                                            const maxTokens = Math.max(
                                                ...data.execution_groups.map(
                                                    (g) => g.total_tokens
                                                )
                                            );
                                            const barWidth =
                                                (group.total_tokens / maxTokens) * 100;

                                            return (
                                                <div key={group.template_id}>
                                                    <div
                                                        className="grid grid-cols-12 gap-2 px-5 py-3 items-center hover:bg-slate-50 cursor-pointer transition-colors group"
                                                        onClick={() =>
                                                            toggleGroup(group.template_id)
                                                        }
                                                    >
                                                        <div className="col-span-4 flex items-center gap-2 min-w-0">
                                                            {isExpanded ? (
                                                                <ChevronDown className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                                                            ) : (
                                                                <ChevronRight className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                                                            )}
                                                            <span className="text-xs font-medium text-slate-700 truncate">
                                                                {workflowNames[
                                                                    group.template_id
                                                                ] ||
                                                                    group.template_id.slice(
                                                                        0,
                                                                        8
                                                                    ) + "..."}
                                                            </span>
                                                        </div>
                                                        <div className="col-span-2 text-right">
                                                            <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                                                                <Zap className="h-3 w-3 text-amber-500" />
                                                                {group.execution_count}
                                                            </span>
                                                        </div>
                                                        <div className="col-span-2 text-right text-xs text-slate-600">
                                                            {formatTokenCount(
                                                                group.total_prompt_tokens
                                                            )}
                                                        </div>
                                                        <div className="col-span-2 text-right text-xs text-slate-600">
                                                            {formatTokenCount(
                                                                group.total_completion_tokens
                                                            )}
                                                        </div>
                                                        <div className="col-span-2 text-right">
                                                            <span className="text-xs font-semibold text-slate-800">
                                                                {formatTokenCount(
                                                                    group.total_tokens
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Progress bar row */}
                                                    <div className="px-5 pb-2 -mt-1">
                                                        <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                                                            <div
                                                                className="h-full rounded-full bg-blue-500 transition-all"
                                                                style={{
                                                                    width: `${barWidth}%`,
                                                                }}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Expanded details */}
                                                    {isExpanded && (
                                                        <div className="px-5 pb-4 bg-slate-50/50">
                                                            <div className="ml-5 space-y-2">
                                                                {/* Models */}
                                                                <div>
                                                                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                                                                        Models Used
                                                                    </p>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {group.model_breakdown.map(
                                                                            (m, i) => (
                                                                                <div
                                                                                    key={`${m.model_name}-${i}`}
                                                                                    className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2"
                                                                                >
                                                                                    <div
                                                                                        className="w-2 h-2 rounded-full"
                                                                                        style={{
                                                                                            backgroundColor:
                                                                                                getModelColor(
                                                                                                    m.model_name,
                                                                                                    i
                                                                                                ),
                                                                                        }}
                                                                                    />
                                                                                    <span className="text-xs text-slate-700 font-medium">
                                                                                        {
                                                                                            m.model_name
                                                                                        }
                                                                                    </span>
                                                                                    <span className="text-[10px] text-slate-400">
                                                                                        {formatTokenCount(
                                                                                            m.total_tokens
                                                                                        )}{" "}
                                                                                        ({m.usage_count}{" "}
                                                                                        calls)
                                                                                    </span>
                                                                                </div>
                                                                            )
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {/* Node types */}
                                                                <div>
                                                                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                                                                        Node Types
                                                                    </p>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {group.node_type_breakdown.map(
                                                                            (n, i) => (
                                                                                <div
                                                                                    key={`${n.node_type}-${n.model_name}-${i}`}
                                                                                    className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2"
                                                                                >
                                                                                    <Box className="h-3 w-3 text-slate-400" />
                                                                                    <span className="text-xs text-slate-700 font-medium">
                                                                                        {NODE_TYPE_NAMES[
                                                                                            n.node_type
                                                                                        ] ||
                                                                                            n.node_type}
                                                                                    </span>
                                                                                    <span className="text-[10px] text-slate-400">
                                                                                        {formatTokenCount(
                                                                                            n.total_tokens
                                                                                        )}
                                                                                    </span>
                                                                                </div>
                                                                            )
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                        </div>
                    )}

                    {!loading && !data && (
                        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                            <BarChart3 className="h-8 w-8 mb-2" />
                            <p className="text-sm">No analytics data available</p>
                            <p className="text-xs mt-1">
                                Try adjusting the date range and click Apply
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TokenAnalyticsDashboard;
